import {
    type InfiniteData,
    queryOptions as defineQueryOptions,
    type UseInfiniteQueryOptions,
    type UseMutationOptions,
    type UseQueryOptions,
} from '@tanstack/react-query';
import axios from 'axios';
import z from 'zod';

import { commonResponses } from '@@api/commonResponses';
import { CONTENT_LANGUAGE_HEADER } from '@@api/constants/headers';
import {
    defaultHeaders,
    DefaultHeaders,
    FileUploadHeaders,
    LoginHeaders,
    PatchDefaultHeaders,
} from '@@api/headers';

import {
    type AxiosResponseUnion,
    type DefineEndpointFnReturn,
    type DefineEndpointOptions,
    type EndpointConfig,
    type EndpointData,
    type ErrorResponses,
    type Methods,
    type OnUploadProgress,
    type StatusCode,
    type SuccessResponses,
} from './types';
import {
    buildUrl,
    processBody,
    processErrorResponse,
    processHeaders,
    processQuery,
    processSuccessResponse,
} from './utils';

export const defineEndpoint = <
    Method extends Methods,
    Path extends `/${string}`,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    options: DefineEndpointOptions,
): DefineEndpointFnReturn<Method, Path, Query, Headers, Body, Responses> => {
    // The endpoint's path is relative to its service. We combine it with the service's baseUrl
    const fullPath = `${options.baseUrl}${config.path}`;

    // We always want to have some defaults in place, but allow overrides
    const combinedHeadersSchema = z.object({
        ...DefaultHeaders.shape,
        ...(config.method === 'PATCH' && PatchDefaultHeaders.shape),
        ...(config.method === 'POST' &&
            config.contentType === 'multipart/form-data' &&
            FileUploadHeaders.shape),
        ...(config.method === 'POST' &&
            config.contentType === 'application/x-www-form-urlencoded' &&
            LoginHeaders.shape),
        ...config.headers?.shape,
    });

    // We always want to have some defaults in place, but allow overrides
    const combinedResponsesSchemas = {
        ...commonResponses,
        ...config.responses,
    };

    const processedConfig: EndpointConfig<Method, Path, Query, Headers, Body, Responses> = {
        ...config,
        path: fullPath,
        headers: combinedHeadersSchema,
        responses: combinedResponsesSchemas,
    };

    if (processedConfig.method === 'GET' || processedConfig.method === 'HEAD') {
        const queryKey = (endpointData: EndpointData<typeof processedConfig>) => [
            processedConfig.path,
            {
                ...(!config.omitContentLanguageFromKey && {
                    [CONTENT_LANGUAGE_HEADER]: defaultHeaders()[CONTENT_LANGUAGE_HEADER],
                }),
                // The headers the query passes win, as in its request
                ...endpointData.headers,
            },
            endpointData.params,
            endpointData.query,
        ];

        return {
            /**
             * This is useful in cases where only the queryKey is needed, without any other option.
             * Some uses cases are for query invalidation after a mutation, where that query is not
             * used within the same component.
             */
            queryKey,

            /**
             * Provides queryOptions to be used with ${@link useQuery}
             */
            queryOptions: (
                endpointData: EndpointData<
                    EndpointConfig<Method, Path, Query, Headers, Body, Responses>
                >,
                queryOptions?: Omit<
                    UseQueryOptions<
                        SuccessResponses<Responses>,
                        ErrorResponses<Responses>,
                        SuccessResponses<Responses>,
                        unknown[]
                    >,
                    'queryKey' | 'queryFn'
                >,
            ) =>
                defineQueryOptions({
                    ...queryOptions,
                    queryKey: queryKey(endpointData),

                    queryFn: async () => {
                        const recomposedPath = buildUrl(processedConfig.path, endpointData.params);

                        const processedBody = processBody(processedConfig, endpointData);
                        const processedQuery = processQuery(processedConfig, endpointData);
                        const processedHeaders = processHeaders(
                            processedConfig,
                            endpointData,
                            recomposedPath,
                        );

                        try {
                            const result = await axios.request<AxiosResponseUnion<Responses>>({
                                url: recomposedPath,
                                method: processedConfig.method,
                                headers: processedHeaders,
                                data: processedBody,
                                params: processedQuery,
                            });

                            return processSuccessResponse(processedConfig, result);
                        } catch (error) {
                            const processedResponse = await processErrorResponse(
                                processedConfig,
                                error,
                            );

                            return processedResponse;
                        }
                    },
                }),

            /**
             * Provides infiniteQueryOptions to be used with ${@link useInfiniteQuery}
             */
            infiniteQueryOptions: (
                endpointData: EndpointData<
                    EndpointConfig<Method, Path, Query, Headers, Body, Responses>
                >,
                infiniteQueryOptions?: Omit<
                    UseInfiniteQueryOptions<
                        SuccessResponses<Responses>,
                        ErrorResponses<Responses>,
                        InfiniteData<SuccessResponses<Responses>>,
                        unknown[],
                        number
                    >,
                    'queryKey' | 'queryFn'
                >,
            ): UseInfiniteQueryOptions<
                SuccessResponses<Responses>,
                ErrorResponses<Responses>,
                InfiniteData<SuccessResponses<Responses>>,
                unknown[],
                number
            > => ({
                getNextPageParam: (lastPage) =>
                    // @ts-expect-error TS2322 Issue #4120
                    lastPage.body.last ? undefined : lastPage.body.number + 1,
                initialPageParam: 0,
                ...infiniteQueryOptions,
                queryKey: queryKey(endpointData),
                queryFn: async (context) => {
                    const endpointDataWithPage = {
                        ...endpointData,
                        query: {
                            ...endpointData.query,
                            page: context.pageParam || 0,
                        },
                    };
                    const recomposedPath = buildUrl(processedConfig.path, endpointData.params);

                    const processedBody = processBody(processedConfig, endpointData);
                    const processedQuery = processQuery(processedConfig, endpointDataWithPage);
                    const processedHeaders = processHeaders(
                        processedConfig,
                        endpointData,
                        recomposedPath,
                    );

                    try {
                        const result = await axios.request<AxiosResponseUnion<Responses>>({
                            url: recomposedPath,
                            method: processedConfig.method,
                            headers: processedHeaders,
                            data: processedBody,
                            params: processedQuery,
                        });

                        return processSuccessResponse(processedConfig, result);
                    } catch (error) {
                        const processedResponse = await processErrorResponse(
                            processedConfig,
                            error,
                        );
                        return processedResponse;
                    }
                },
            }),
        } as unknown as DefineEndpointFnReturn<Method, Path, Query, Headers, Body, Responses>;
    }

    return {
        mutationOptions: (
            mutationOptions?: Omit<
                UseMutationOptions<
                    SuccessResponses<Responses>,
                    ErrorResponses<Responses>,
                    EndpointData<EndpointConfig<Method, Path, Query, Headers, Body, Responses>>
                >,
                'mutationKey' | 'mutationFn'
            >,
        ): UseMutationOptions<
            SuccessResponses<Responses>,
            ErrorResponses<Responses>,
            EndpointData<EndpointConfig<Method, Path, Query, Headers, Body, Responses>> & {
                onUploadProgress?: OnUploadProgress;
            }
        > => ({
            ...mutationOptions,
            mutationFn: async (
                endpointData: EndpointData<
                    EndpointConfig<Method, Path, Query, Headers, Body, Responses>
                > & {
                    onUploadProgress?: OnUploadProgress;
                },
            ) => {
                const recomposedPath = buildUrl(processedConfig.path, endpointData.params);

                const processedBody = processBody(processedConfig, endpointData);
                const processedQuery = processQuery(processedConfig, endpointData);
                const processedHeaders = processHeaders(
                    processedConfig,
                    endpointData,
                    recomposedPath,
                );

                try {
                    const result = await axios.request<AxiosResponseUnion<Responses>>({
                        url: recomposedPath,
                        method: processedConfig.method,
                        headers: processedHeaders,
                        data: processedBody,
                        params: processedQuery,
                    });

                    return processSuccessResponse(processedConfig, result);
                } catch (error) {
                    const processedResponse = await processErrorResponse(processedConfig, error);

                    return processedResponse;
                }
            },
        }),
    } as unknown as DefineEndpointFnReturn<Method, Path, Query, Headers, Body, Responses>;
};
